<script setup lang="ts">
import { GroupOption, ItemOption } from "../MSelect";
import MLayoutStack from "../../MLayoutStack";
import { nextTick, watch } from "vue";
import { makeHtmlIdSafe } from "../../../../utils/strings";

const props = defineProps<{
  focusedItem: any;
  items: Array<GroupOption | ItemOption>;
  value: any;
  disabled?: boolean;
}>();

const emit = defineEmits({
  select: (_options: ItemOption) => true,
});

function onSelect(option: ItemOption) {
  emit("select", option);
}

watch(
  () => props.focusedItem,
  (v) =>
    nextTick(() => {
      document.getElementById(`select-item-${v.value}`)?.scrollIntoView({ block: "end" });
    }),
);
</script>
<template>
  <div class="select-render-list">
    <template v-for="(item, k) in items" :key="`item-outer-${k}${item.title}${item.label}`">
      <template v-if="'title' in item">
        <!-- ENG-15784 WCAG correctly group `listitems` with role="group", role="presentation" and aria-labelledby        -->
        <component
          :is="'title' in item ? 'div' : 'template'"
          class="group-wrapper m-ma-none m-pa-none"
          role="group"
          :aria-labelledby="`group-id-${k}-${makeHtmlIdSafe(item.title)}`"
        >
          <div
            :id="`group-id-${k}-${makeHtmlIdSafe(item.title)}`"
            :class="['simple-select-item', 'title', disabled]"
            role="presentation"
          >
            {{ item.title }}
          </div>
          <m-layout-stack
            v-for="(subItem, si) in item.items"
            :id="`select-item-${subItem.value}`"
            :key="`item-inner-${subItem.label}-${si}`"
            :class="{
              'simple-select-item': true,
              selected: subItem.value === value,
              focused: subItem === focusedItem || subItem.value === focusedItem.value,
              title: 'title' in subItem,
              disabled,
            }"
            :aria-selected="subItem.value === value ?? null"
            horizontal
            justify-between
            role="option"
            @click="onSelect(subItem)"
          >
            <div
              :class="[
                subItem.color && `color-${subItem.color}`,
                subItem.description && 'm-select-item-with-description',
              ]"
            >
              <div v-if="subItem.description">
                <p class="m-select-item-label">
                  {{ subItem.label }}
                </p>
                <template v-if="Array.isArray(subItem.description)">
                  <p
                    v-for="d in subItem.description"
                    :key="`${subItem.label}.${d}`"
                    class="m-select-item-description"
                  >
                    {{ d }}
                  </p>
                </template>

                <p v-else class="m-select-item-description">
                  {{ subItem.description }}
                </p>
              </div>
              <template v-else>
                {{ subItem.label }}
              </template>
            </div>

            <div v-if="subItem.value === value" aria-hidden="true">
              <i aria-hidden="true" class="fa-light fa-check" />
            </div>
          </m-layout-stack>
        </component>
      </template>
      <!--   Simple list of items, no groups   -->
      <m-layout-stack
        v-else
        :id="`select-item-${item.value}`"
        :class="{
          'simple-select-item': true,
          selected: item.value === value,
          focused: item === focusedItem || item.value === focusedItem.value,
          title: 'title' in item,
        }"
        horizontal
        justify-between
        role="option"
        @click="onSelect(item)"
      >
        <div
          :class="[
            item.color && `color-${item.color}`,
            item.description && 'm-select-item-with-description',
          ]"
        >
          <div v-if="item.description">
            <p class="m-select-item-label">
              {{ item.label }}
            </p>
            <template v-if="Array.isArray(item.description)">
              <p
                v-for="d in item.description"
                :key="`${item.label}.${d}`"
                class="m-select-item-description"
              >
                {{ d }}
              </p>
            </template>

            <p v-else class="m-select-item-description">
              {{ item.description }}
            </p>
          </div>
          <template v-else>
            {{ item.label }}
          </template>
        </div>

        <div v-if="item.value === value">
          <i aria-hidden="true" class="fa-light fa-check" />
        </div>
      </m-layout-stack>
    </template>
  </div>
</template>
<style lang="scss">
@import "SelectRenderList.scss";
</style>
